import {
  ReactNode,
  createContext,
  useEffect,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
} from 'react'
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material'

import { STORAGE_KEYS } from 'src/constants'
import { useMe, useSetDarkMode } from 'src/hooks'
import { breakpointValues, createPalette } from 'src/containers/theme'
import { IWhiteLabelForm } from 'src/types/white-label-partners.type'

interface IThemeProviderProps {
  children: ReactNode
}

interface IThemeSwitchContext {
  isDark: boolean
  setIsDark: (value: boolean) => void
  auto_theme: boolean
  userTheme: boolean
  whiteLabel: IWhiteLabelForm | undefined
  setWhiteLable: Dispatch<SetStateAction<IWhiteLabelForm | undefined>>
}

interface IThemeProviderProps {
  children: ReactNode
}

export const ThemeSwitchContext = createContext<IThemeSwitchContext>({} as IThemeSwitchContext)

const ThemeProvider = ({ children }: IThemeProviderProps) => {
  let darkTheme = false
  try {
    darkTheme = JSON.parse(localStorage.getItem(STORAGE_KEYS.DARK_THEME)!)
  } catch {}

  const [isDark, setIsDark] = useState(darkTheme)
  const { data: me, isLoading } = useMe()
  const { mutate: setDarkMode } = useSetDarkMode()
  const [whiteLabel, setWhiteLable] = useState<IWhiteLabelForm | undefined>(me?.white_label)

  // const theme = useChangeTheme(me?.white_label);

  // const { auto_theme, userTheme } = useContext(ThemeSwitchContext)

  const theme = useMemo(() => {
    return createTheme({
      palette: createPalette(
        isDark,
        whiteLabel ? whiteLabel : me?.white_label ? me.white_label : undefined,
      ),
      typography: {
        fontFamily: ['Noto Sans', 'sans-serif'].join(','),
      },
      breakpoints: {
        values: breakpointValues,
      },
    })
  }, [isDark, whiteLabel, me?.white_label])

  useEffect(() => {
    if (!me) return
    if (me.auto_theme) {
      setDarkMode(true)
      setIsDark(true)
    }
  }, [me, setDarkMode, setIsDark])

  const handleThemeChange = (isDark: boolean) => {
    setDarkMode(isDark)
    setIsDark(isDark)
    localStorage.setItem(STORAGE_KEYS.DARK_THEME, JSON.stringify(isDark))
  }

  if (isLoading) return null

  return (
    <ThemeSwitchContext.Provider
      value={{
        isDark,
        setIsDark: handleThemeChange,
        auto_theme: !!me?.auto_theme,
        userTheme: !!me?.dark_mode,
        whiteLabel: whiteLabel,
        setWhiteLable,
      }}
    >
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeSwitchContext.Provider>
  )
}

export default ThemeProvider
